import { EBotExamType } from '../types/EBotExamType';
import type { QuestionTypeFilter } from '../types/revision';

/**
 * Chave i18n (namespace `examRevision`) do rótulo de um tipo de questão ou do
 * filtro "Todos". Os rótulos de tipo reaproveitam `visualize.type.*` — fonte
 * única e alinhada ao enum {@link EBotExamType} — para evitar divergência.
 */
export type QuestionTypeI18nKey =
  | 'review.filterTypes.all'
  | 'visualize.type.default'
  | 'visualize.type.trueFalse'
  | 'visualize.type.multipleAffirmations'
  | 'visualize.type.fillInTheBlanks';

/**
 * Ordem dos filtros de tipo exibidos: "Todos" seguido de cada valor do enum
 * {@link EBotExamType}. Derivado do enum — adicionar um tipo aqui é suficiente
 * para aparecer no filtro.
 */
export const QUESTION_TYPE_FILTERS: readonly QuestionTypeFilter[] = [
  'all',
  EBotExamType.Default,
  EBotExamType.TrueFalse,
  EBotExamType.MultipleAffirmations,
  EBotExamType.FillInTheBlanks,
];

/**
 * Converte o `type` numérico (`EBotExamType`) de um DTO para o enum tipado.
 * Valores desconhecidos/ausentes caem em {@link EBotExamType.Default}.
 *
 * @param type - Código do tipo de questão vindo da API
 * @returns Valor tipado de {@link EBotExamType}
 */
export function mapQuestionType(type: number | null | undefined): EBotExamType {
  switch (type) {
    case EBotExamType.TrueFalse:
      return EBotExamType.TrueFalse;
    case EBotExamType.MultipleAffirmations:
      return EBotExamType.MultipleAffirmations;
    case EBotExamType.FillInTheBlanks:
      return EBotExamType.FillInTheBlanks;
    default:
      return EBotExamType.Default;
  }
}

/**
 * Chave i18n do rótulo de um tipo de questão.
 *
 * @param type - Tipo da questão
 * @returns Chave i18n do rótulo correspondente
 */
export function questionTypeI18nKey(type: EBotExamType): QuestionTypeI18nKey {
  switch (type) {
    case EBotExamType.TrueFalse:
      return 'visualize.type.trueFalse';
    case EBotExamType.MultipleAffirmations:
      return 'visualize.type.multipleAffirmations';
    case EBotExamType.FillInTheBlanks:
      return 'visualize.type.fillInTheBlanks';
    default:
      return 'visualize.type.default';
  }
}

/**
 * Chave i18n do rótulo de uma opção de filtro de tipo (inclui "Todos").
 *
 * @param filter - Opção de filtro selecionada
 * @returns Chave i18n do rótulo correspondente
 */
export function questionFilterI18nKey(filter: QuestionTypeFilter): QuestionTypeI18nKey {
  return filter === 'all' ? 'review.filterTypes.all' : questionTypeI18nKey(filter);
}
